import {
  ArrowLeftIcon,
  CameraIcon,
  EmojiHappyIcon,
  MicrophoneIcon,
  PaperClipIcon,
  PhoneIcon,
} from '@heroicons/react/outline';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

export type WhatsAppLine =
  | { from: 'customer'; text: ReactNode; at: number; time: string }
  | {
      from: 'agent';
      text: ReactNode;
      at: number;
      time: string;
      /** When set, WhatsApp's typing bubble runs from here until `at`. */
      typingAt?: number;
    };

/**
 * A customer's WhatsApp chat with a business whose number the agent
 * answers, in WhatsApp's own colours. The customer's messages are theirs, so
 * they sit on the outgoing (green) side.
 */
export default function WhatsAppChat({
  brand,
  lines,
}: {
  brand: DemoBrand;
  lines: WhatsAppLine[];
}) {
  const { t } = useChannelsT();

  return (
    <PhoneFrame bar='bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]'>
      <div className='flex items-center gap-2.5 bg-white px-3 pb-2.5 pt-1 text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]'>
        <ArrowLeftIcon className='h-5 w-5 flex-none text-[#54656f] rtl:-scale-x-100 dark:text-[#aebac1]' />
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-[15px] font-semibold text-white',
            brand.bg,
          )}
        >
          {brand.initial}
        </span>
        <span className='min-w-0 flex-1'>
          <span className='block truncate text-[15px] font-semibold leading-tight'>
            {brand.name}
          </span>
          <span className='block truncate text-[11.5px] leading-tight text-[#667781] dark:text-[#8696a0]'>
            {t('Business account')}
          </span>
        </span>
        <PhoneIcon className='h-[18px] w-[18px] flex-none text-[#54656f] dark:text-[#aebac1]' />
      </div>

      <div className='relative flex min-h-[25rem] flex-col justify-end gap-1.5 bg-[#efeae2] px-3 pb-2 pt-3 text-[13.5px] leading-[1.35] text-[#111b21] dark:bg-[#0b141a] dark:text-[#e9edef]'>
        <Wallpaper />
        <p
          className={clsxm(
            styles.enter,
            'relative mx-auto mb-1 rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:bg-[#182229] dark:text-[#8696a0]',
          )}
          style={delay(150)}
        >
          {t('Today')}
        </p>
        <p
          className={clsxm(
            styles.enter,
            'relative mx-auto mb-2 max-w-[92%] rounded-md bg-[#ffeecd] px-2.5 py-1.5 text-center text-[11px] leading-snug text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:bg-[#182229] dark:text-[#ffd279]',
          )}
          style={delay(250)}
        >
          {t(
            'This business uses a secure service from Meta to manage this chat.',
          )}
        </p>

        {lines.map((line, index) => {
          const previous = lines[index - 1];
          const tail = !previous || previous.from !== line.from;
          return line.from === 'customer' ? (
            <Bubble key={index} out tail={tail} at={line.at} time={line.time}>
              {line.text}
            </Bubble>
          ) : (
            <div key={index} className='relative grid'>
              {line.typingAt !== undefined && (
                <span
                  className={clsxm(
                    styles.typing,
                    'relative ms-2 flex h-8 w-14 items-center justify-center gap-1 self-start rounded-[10px] rounded-ss-none bg-white shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] [grid-area:1/1] dark:bg-[#202c33]',
                  )}
                  style={delay(line.typingAt, {
                    '--len': `${line.at - line.typingAt}ms`,
                  })}
                >
                  <Tail out={false} />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8696a0]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8696a0]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#8696a0]' />
                </span>
              )}
              <div className='[grid-area:1/1]'>
                <Bubble tail={tail} at={line.at} time={line.time}>
                  {line.text}
                </Bubble>
              </div>
            </div>
          );
        })}
      </div>

      <div className='flex items-center gap-1.5 bg-[#efeae2] px-2 pb-4 pt-1 dark:bg-[#0b141a]'>
        <span className='flex h-10 min-w-0 flex-1 items-center gap-2 rounded-full bg-white px-3 text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:bg-[#2a3942] dark:text-[#8696a0]'>
          <EmojiHappyIcon className='h-5 w-5 flex-none' />
          <span className='min-w-0 flex-1 truncate text-[14px] text-[#8696a0]'>
            {t('Message')}
          </span>
          <PaperClipIcon className='h-[18px] w-[18px] flex-none -rotate-45' />
          <CameraIcon className='h-[18px] w-[18px] flex-none' />
        </span>
        <span className='flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#00a884] text-white dark:text-[#111b21]'>
          <MicrophoneIcon className='h-5 w-5' />
        </span>
      </div>
    </PhoneFrame>
  );
}

function Bubble({
  out = false,
  tail,
  at,
  time,
  children,
}: {
  /** The customer's own message, on the green side. */
  out?: boolean;
  /** The first message in a run carries WhatsApp's little tail. */
  tail: boolean;
  at: number;
  time: string;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm('relative flex', out ? 'justify-end' : 'justify-start')}
    >
      <div
        className={clsxm(
          styles.bubble,
          out ? styles.bubbleEnd : styles.bubbleStart,
          'relative max-w-[84%] break-words rounded-[10px] px-2 pb-[7px] pt-1.5 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]',
          out
            ? 'me-2 bg-[#d9fdd3] dark:bg-[#005c4b]'
            : 'ms-2 bg-white dark:bg-[#202c33]',
          tail && (out ? 'rounded-se-none' : 'rounded-ss-none'),
        )}
        style={delay(at)}
      >
        {tail && <Tail out={out} />}
        {children}
        {/* Room for the time on the last line, as WhatsApp leaves it. */}
        <span
          aria-hidden='true'
          className={clsxm('inline-block', out ? 'w-[58px]' : 'w-[40px]')}
        />
        {/* `end` follows the page's direction; only the digits stay LTR. */}
        <span
          className={clsxm(
            'absolute bottom-1 end-2 text-[10.5px] leading-none tabular-nums',
            out
              ? 'text-[#667781] dark:text-white/60'
              : 'text-[#667781] dark:text-[#8696a0]',
          )}
        >
          <span dir='ltr' className='flex items-center gap-[3px]'>
            {time}
            {out && <ReadTicks />}
          </span>
        </span>
      </div>
    </div>
  );
}

/** The bubble's tail: on the end side for the customer, the start side for the business. */
function Tail({ out }: { out: boolean }) {
  return (
    <svg
      viewBox='0 0 8 13'
      aria-hidden='true'
      className={clsxm(
        'absolute top-0 h-[13px] w-2',
        out
          ? '-end-2 text-[#d9fdd3] rtl:-scale-x-100 dark:text-[#005c4b]'
          : '-start-2 -scale-x-100 text-white rtl:scale-x-100 dark:text-[#202c33]',
      )}
    >
      <path d='M0 0h6.2c1.6 0 2.2 1.6 1.2 2.8L0 12.6V0z' fill='currentColor' />
    </svg>
  );
}

function ReadTicks() {
  return (
    <svg
      viewBox='0 0 16 11'
      aria-hidden='true'
      className='h-[11px] w-4 text-[#53bdeb]'
    >
      <path
        d='M11.1.6 4.6 7.8 1.7 5 .6 6.1l4 3.9L12.2 1.7 11.1.6zm3.8 0L8.4 7.8l-.9-.9-1.1 1.1 2 2L16 1.7 14.9.6z'
        fill='currentColor'
      />
    </svg>
  );
}

/** A faint pattern standing in for WhatsApp's doodle wallpaper. */
function Wallpaper() {
  return (
    <span
      aria-hidden='true'
      className='pointer-events-none absolute inset-0 opacity-[0.55] [background-image:radial-gradient(rgba(84,101,111,0.13)_1px,transparent_1.4px),radial-gradient(rgba(84,101,111,0.08)_1px,transparent_1.4px)] [background-position:0_0,11px_11px] [background-size:22px_22px] dark:opacity-[0.35] dark:[background-image:radial-gradient(rgba(233,237,239,0.09)_1px,transparent_1.4px),radial-gradient(rgba(233,237,239,0.05)_1px,transparent_1.4px)]'
    />
  );
}
